import {
  AppointmentTaskProxyFragment,
  Resource_AppointmentTaskDueStage,
  Resource_AppointmentTaskStatus,
  useListAppointmentTasksQuery,
} from '@/generated/graphql';
import { AppointmentTaskContainer } from '@components/appointment-details/appointment-tasks/appointment-task-container';
import { availableAppointmentTaskTypes } from '@components/appointment-details/appointment-tasks/available-appointment-task-types';
import { AppointmentTaskTypeToIcon } from '@components/appointment-details/appointment-tasks/icons';
import { AppointmentTaskStatusColors } from '@components/appointment-details/appointment-tasks/status-colors';
import { Button, Card, List, Modal, Space, Tooltip } from 'antd';
import Title from 'antd/lib/typography/Title';
import { useRouter } from 'next/router';
import { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { extractTranslation } from '../../../helpers/formatters';
import styles from './appointment-tasks.module.scss';

interface AppointmentTaskListItemProps {
  proxyAppointmentTask: AppointmentTaskProxyFragment;
  onClick: () => void;
}

const AppointmentTaskListItem: FC<AppointmentTaskListItemProps> = ({
  proxyAppointmentTask,
  onClick,
}) => {
  const router = useRouter();
  const { formatMessage } = useIntl();

  const appointmentTask = proxyAppointmentTask.appointmentTask;
  const taskType = appointmentTask?.type;
  const title = appointmentTask?.title;
  const subtitle = appointmentTask?.subtitle;

  const status =
    appointmentTask?.status ?? Resource_AppointmentTaskStatus.AppointmentTaskStatusNone;
  const TaskTypeIcon = taskType ? AppointmentTaskTypeToIcon.get(taskType) : undefined;

  const cancelled = status === Resource_AppointmentTaskStatus.AppointmentTaskStatusCanceled;

  return (
    <List.Item
      onClick={onClick}
      actions={
        !cancelled
          ? [
              <Button key={'open'} type={'link'} onClick={onClick}>
                {formatMessage({ id: 'action.open' })}
              </Button>,
            ]
          : []
      }
    >
      <List.Item.Meta
        className={styles.listItem}
        avatar={
          TaskTypeIcon && (
            <Tooltip title={taskType && formatMessage({ id: taskType })}>
              <TaskTypeIcon
                style={{
                  fontSize: '1.5em',
                  color: '#ffffff', // Icon color
                  backgroundColor: AppointmentTaskStatusColors.get(status), // Background color
                  borderRadius: '50%', // Make the background circular
                  padding: '10px', // Adjust padding for size
                }}
              />
            </Tooltip>
          )
        }
        title={
          extractTranslation(title, router.locale) ??
          formatMessage({ id: 'appointment-tasks.title-unknown' })
        }
        description={
          <div>
            <div>{extractTranslation(subtitle, router.locale)}</div>
            {cancelled && (
              <div style={{ color: 'darkgray' }}>
                {formatMessage(
                  { id: 'appointment-tasks.reason-for-cancellation' },
                  {
                    reasons:
                      appointmentTask?.reasonsForCancellationExternal
                        ?.map((reason) => reason?.text)
                        ?.join(', ') ||
                      formatMessage({ id: 'appointment-tasks.reason-for-cancellation.missing' }),
                  },
                )}
              </div>
            )}
          </div>
        }
      />
    </List.Item>
  );
};

interface AppointmentTasksProps {
  clinicId: string;
  appointmentId: string;
}

export const AppointmentTasks: FC<AppointmentTasksProps> = ({ clinicId, appointmentId }) => {
  const { formatMessage } = useIntl();

  // Must be the id since the cached entry does not change
  const [selectedAppointmentTaskId, setSelectedAppointmentTaskId] = useState<string>();

  const { data: appointmentTasksData, loading: appointmentTasksLoading } =
    useListAppointmentTasksQuery({
      variables: {
        clinicId,
        request: {
          appointmentId,
        },
      },
    });

  // Filtered list of proxy appointments. Edit available-appointment-task-types if to be extended
  const filteredProxyAppointmentTasks = useMemo(
    () =>
      appointmentTasksData?.listAppointmentTasks?.proxyAppointmentTasks
        ?.filterPresent()
        .filter(
          (proxyAppointmentTask) =>
            proxyAppointmentTask.appointmentTask?.type &&
            availableAppointmentTaskTypes.includes(proxyAppointmentTask.appointmentTask.type),
        ) ?? [],
    [appointmentTasksData?.listAppointmentTasks?.proxyAppointmentTasks],
  );

  const selectedProxyAppointmentTask = useMemo(
    () =>
      selectedAppointmentTaskId
        ? filteredProxyAppointmentTasks.find(
            (proxyAppointmentTask) =>
              proxyAppointmentTask.appointmentTask?.id === selectedAppointmentTaskId,
          )
        : undefined,
    [selectedAppointmentTaskId, filteredProxyAppointmentTasks],
  );

  const proxyAppointmentTasksByDueStage = filteredProxyAppointmentTasks.groupBy(
    (proxyAppointmentTask) =>
      proxyAppointmentTask.appointmentTask?.dueStage ??
      Resource_AppointmentTaskDueStage.AppointmentTaskDueStageNone,
  );

  if (!appointmentTasksLoading && !filteredProxyAppointmentTasks.length) {
    return null;
  }

  return (
    <Card title={formatMessage({ id: 'appointment-tasks' })}>
      <Space direction={'vertical'} style={{ width: '100%' }}>
        {[
          Resource_AppointmentTaskDueStage.AppointmentTaskDueStageBeforeAppointment,
          Resource_AppointmentTaskDueStage.AppointmentTaskDueStageDuringAppointment,
          Resource_AppointmentTaskDueStage.AppointmentTaskDueStageAfterAppointment,
        ]
          .filter((dueStage) => !!proxyAppointmentTasksByDueStage.get(dueStage)?.length)
          .map((dueStage) => (
            <div key={dueStage}>
              <Title level={3}>{formatMessage({ id: dueStage })}</Title>

              <List
                loading={appointmentTasksLoading}
                dataSource={proxyAppointmentTasksByDueStage.get(dueStage)}
                renderItem={(proxyAppointmentTask) => (
                  <AppointmentTaskListItem
                    proxyAppointmentTask={proxyAppointmentTask}
                    onClick={() => {
                      if (
                        proxyAppointmentTask.appointmentTask?.id &&
                        proxyAppointmentTask?.appointmentTask?.status !==
                          Resource_AppointmentTaskStatus.AppointmentTaskStatusCanceled
                      ) {
                        setSelectedAppointmentTaskId(proxyAppointmentTask.appointmentTask.id);
                      }
                    }}
                  />
                )}
              />
            </div>
          ))}
      </Space>

      {selectedProxyAppointmentTask && (
        <Modal
          footer={false}
          title={null}
          open={!!selectedProxyAppointmentTask}
          onCancel={() => setSelectedAppointmentTaskId(undefined)}
        >
          <AppointmentTaskContainer
            proxyAppointmentTask={selectedProxyAppointmentTask}
            onComplete={() => setSelectedAppointmentTaskId(undefined)}
          />
        </Modal>
      )}
    </Card>
  );
};
